import React from 'react';
import { Box, InputBase, NativeSelect, Typography } from '@mui/material';
import { WarningAmberRounded } from '@mui/icons-material';
import { Lookup } from '@views';
import { DURATION_OPTIONS, FREQUENCY_OPTIONS, GoalActivity, NO_ACTIVITY } from '../smartGoalUtils';
import { tokens } from './tokens';

const fieldLabelSx = {
  fontSize: '13px',
  fontWeight: 600,
  color: tokens.label,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const FormSelect: React.FC<{
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  children: React.ReactNode;
}> = ({ id, label, value, onChange, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
    <Box component="label" htmlFor={id} sx={fieldLabelSx}>
      {label}
    </Box>
    <NativeSelect
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      input={<InputBase />}
      inputProps={{ id, name: id }}
      sx={{
        '& .MuiNativeSelect-select': {
          border: `1px solid ${tokens.border}`,
          borderRadius: '12px',
          py: '10px',
          pl: '14px',
          pr: '32px !important',
          fontSize: '15px',
          background: '#fff',
          color: tokens.textPrimary,
        },
        '& .MuiNativeSelect-select:focus': { borderRadius: '12px', background: '#fff', borderColor: 'primary.main' },
        '& .MuiNativeSelect-icon': { right: 8 },
      }}
    >
      {children}
    </NativeSelect>
  </Box>
);

const Divider = () => <Box sx={{ height: '1px', background: tokens.borderSubtle }} />;

export const GoalFormCard: React.FC<{
  form: GoalActivity;
  activityOptions: Lookup[];
  onActivityChange: (id: number) => void;
  onFrequencyChange: (n: number) => void;
  onDurationChange: (n: number) => void;
}> = ({ form, activityOptions, onActivityChange, onFrequencyChange, onDurationChange }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      background: tokens.surface,
      borderRadius: '16px',
      p: 2,
      border: `1px solid ${tokens.borderSubtle}`,
    }}
  >
    <FormSelect id="activity" label="Activity" value={form.activityId} onChange={onActivityChange}>
      <option value={NO_ACTIVITY}>Choose an activity…</option>
      {activityOptions.map((a) => (
        <option key={a.id} value={a.id}>
          {a.name}
        </option>
      ))}
    </FormSelect>
    <Divider />
    <FormSelect id="frequency" label="Frequency" value={form.frequency} onChange={onFrequencyChange}>
      {FREQUENCY_OPTIONS.map((n) => (
        <option key={n} value={n}>
          {n}× per week
        </option>
      ))}
    </FormSelect>
    <Divider />
    <FormSelect id="duration" label="Duration" value={form.duration} onChange={onDurationChange}>
      {DURATION_OPTIONS.map((n) => (
        <option key={n} value={n}>
          {n} min
        </option>
      ))}
    </FormSelect>
  </Box>
);

export const DuplicateWarning: React.FC<{ name: string }> = ({ name }) => (
  <Box
    role="alert"
    sx={{
      mt: 1,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      py: 1.25,
      background: tokens.warningBg,
      border: `1px solid ${tokens.warningBorder}`,
      borderRadius: '12px',
    }}
  >
    <WarningAmberRounded sx={{ color: tokens.warningText, fontSize: 18 }} />
    <Typography sx={{ fontSize: '13px', fontWeight: 500, color: tokens.warningText }}>
      <strong>{name}</strong> is already in your goal. Choose a different activity.
    </Typography>
  </Box>
);
